import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as api from './api.ts';
import { Button, Finding } from './ui.tsx';

/**
 * One building, one site (2026-09-29). Two sites for one place split its
 * roster and presence across two counts, so the API refuses a duplicate with
 * a 409 `site_duplicate`. These are the dashboard's halves: ask early (the
 * moment an address is picked or a pin dropped) and read the 409 when a race
 * beats the early answer — both land on the same Finding, which turns the
 * refusal into a destination ("Open <name>") or, for a genuinely different
 * building, the audited override ("It's a different building").
 */

export interface DuplicateSite {
  siteId: string;
  name: string;
  /** The server's own sentence — it differs for an address vs a pin match. */
  message: string;
}

/**
 * The org's existing building this place duplicates, or null. Typing is
 * debounced; a picked pin asks at once. Pending reads as "no match", so a slow
 * or failed check never blocks Create — the server's 409 is the enforcement.
 */
export function useSiteMatch(
  orgId: string,
  place: {
    address: string;
    unit?: string;
    level?: string;
    coord: { latitude: number; longitude: number } | null;
  },
): DuplicateSite | null {
  const address = place.address.trim();
  const [debounced, setDebounced] = useState(address);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(address), 400);
    return () => clearTimeout(t);
  }, [address]);

  const unit = place.unit?.trim() || undefined;
  const level = place.level?.trim() || undefined;
  const lat = place.coord?.latitude;
  const lon = place.coord?.longitude;
  const enabled = Boolean(orgId) && (debounced.length >= 5 || lat !== undefined);

  const q = useQuery({
    queryKey: ['site-match', orgId, debounced, unit, level, lat, lon],
    queryFn: () =>
      api.matchSite(orgId, {
        ...(debounced ? { address: debounced } : {}),
        ...(unit ? { unit } : {}),
        ...(level ? { level } : {}),
        ...(lat !== undefined && lon !== undefined ? { latitude: lat, longitude: lon } : {}),
      }),
    enabled,
    // No cache: a building added or removed a moment ago must be seen now.
    staleTime: 0,
    retry: false,
  });
  if (!enabled) return null;
  const m = q.data?.match;
  return m ? { siteId: m.siteId, name: m.name, message: m.message } : null;
}

/** The existing site a create/update 409 named, or null for any other error. */
export function duplicateFromError(error: unknown): DuplicateSite | null {
  const detail = (error as { detail?: Record<string, unknown> } | null)?.detail;
  if (
    detail?.code === 'site_duplicate' &&
    typeof detail.existingSiteId === 'string' &&
    typeof detail.existingSiteName === 'string'
  ) {
    return {
      siteId: detail.existingSiteId,
      name: detail.existingSiteName,
      message: error instanceof Error ? error.message : '',
    };
  }
  return null;
}

/**
 * The server's sentence ("This address is already set up as <name>." or the
 * pin-distance wording) with the way there, and — where the caller creates —
 * the audited override for a genuinely different building at the same
 * address or within 25 m (two blocks on one campus, a demountable beside the
 * main wing). A status region, so a screen-reader user hears why Create went
 * grey.
 */
export function DuplicateSiteFinding({
  site,
  onConfirmDistinct,
  confirming = false,
}: {
  site: DuplicateSite;
  onConfirmDistinct?: () => void;
  confirming?: boolean;
}) {
  const open = () => {
    // pushState + a synthetic popstate is the app's own router.
    window.history.pushState({}, '', `/locations/${site.siteId}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };
  return (
    <div role="status" aria-live="polite">
      <Finding>
        <span className="block">
          {site.message || `This address is already set up as ${site.name}.`}
        </span>
        <span className="mt-2 flex flex-wrap items-center gap-2">
          <Button onClick={open}>Open {site.name}</Button>
          {onConfirmDistinct ? (
            <Button
              variant="quiet"
              disabled={confirming}
              label={`It's a different building from ${site.name}, create it anyway`}
              onClick={onConfirmDistinct}
            >
              It&rsquo;s a different building
            </Button>
          ) : null}
        </span>
      </Finding>
    </div>
  );
}
